import { useSession } from "@hooks/useSession";
import { queriesPayStatements } from "@query";
import {
  WSButton,
  WSEmptyState,
  WSFlexBox,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPill,
  WSSectionToolbar,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import orderBy from "lodash/orderBy";
import React from "react";

import { PaystubHistoryTable } from "../../components/PaystubHistoryTable";
import { RecentPayCard } from "../../components/RecentPayCard";
import { PAYSTUBS_YEARS } from "../../constants";
import { useQueryPayerAccounts } from "../../hooks/useQueryPayerAccounts";
import {
  routeHelperPaystubsDashboard,
  routeHelperPaystubsDetails
} from "../../paths";
import { selectorPaystubHistoryRow } from "../../selectors/selectorPaystubHistoryRow";
import { selectorPaystubRecentPay } from "../../selectors/selectorPaystubRecentPay";
import { PaystubsYear } from "../../types";
import { getDefaultPaystubsYear, getPaystubsYearRange } from "../../utils";

const yearOptions = PAYSTUBS_YEARS.map(year => ({
  label: `${year}`,
  value: `${year}`
}));

export const RoutePaystubsDashboard: React.FC = () => {
  const { accountId } = useSession();
  const goToDashboard = routeHelperPaystubsDashboard.useGoTo();
  const goToDetails = routeHelperPaystubsDetails.useGoTo();
  const {
    params: { year: currentYear }
  } = routeHelperPaystubsDashboard.useData();
  const year = (currentYear || getDefaultPaystubsYear()) as PaystubsYear;

  // The payee's own pay statements for the selected year. Whole-list read: the
  // screen needs the full set for the count, the "recent pay" card and history.
  // Scoped to the session account (the payee) and the year's pay-date range.
  const { gte, lte } = getPaystubsYearRange(year);
  const query = queriesPayStatements.listPayStatements.useQueryAll(
    {
      filter: {
        payeeAccountId: { eq: accountId },
        payDate: { gte, lte }
      }
    },
    { enabled: !!accountId }
  );

  // Newest first — the list has no useful server sort, so order client-side.
  const payStatements = orderBy(
    query.flatData ?? [],
    payStatement => payStatement.payDate?.getTime() ?? 0,
    "desc"
  );

  const { accountsById } = useQueryPayerAccounts(
    payStatements.map(payStatement => payStatement.payerAccountId ?? "")
  );

  const recentStatement = payStatements[0];
  const recentPay = recentStatement
    ? selectorPaystubRecentPay(recentStatement)
    : undefined;

  const historyRows = payStatements.map(payStatement =>
    selectorPaystubHistoryRow({
      payStatement,
      payerAccount: accountsById.get(payStatement.payerAccountId ?? "")
    })
  );

  return (
    <WSPage>
      <WSPageToolbar
        actions={[{ label: "Manage payment settings" }]}
        title="Paystubs"
        titleAfter={
          <WSSelect
            hideClearAction={true}
            mode="single"
            options={yearOptions}
            value={year}
            onChange={value => {
              goToDashboard({ year: value as PaystubsYear });
            }}
          />
        }
      />

      {query.isError ? (
        <WSEmptyState
          actions={{
            buttons: [{ label: "Try again", onClick: () => query.refetch() }]
          }}
          description="Something went wrong loading your paystubs."
          mt="XL"
          title="Couldn't load paystubs"
          type="error"
        />
      ) : (
        <WSList gap="XL" mt="L">
          {recentPay && (
            <WSList gap="S">
              <WSSectionToolbar title="Recent pay" />
              <RecentPayCard
                recentPay={recentPay}
                onViewPaystub={() =>
                  goToDetails({ year, paystubId: recentPay.paystubId })
                }
              />
            </WSList>
          )}

          <WSList gap="S">
            <WSSectionToolbar title="Paystub history" />

            <WSFlexBox.CenterY justify="space-between">
              {/* TODO: wire up filtering once filter definitions are available. */}
              <WSButton kind="Secondary" rightIcon="chevron-down" size="S">
                Filters
              </WSButton>

              <WSFlexBox.CenterY>
                <WSText.ParagraphXs color="gray500" mr="S">
                  Showing
                </WSText.ParagraphXs>
                <WSPill text={`${historyRows.length} items`} theme="neutral" />
              </WSFlexBox.CenterY>
            </WSFlexBox.CenterY>

            {!query.isInitialLoading && historyRows.length === 0 ? (
              <WSEmptyState
                description="Paystubs will appear here as you get paid."
                title="No paystubs yet"
                type="documents"
              />
            ) : (
              <PaystubHistoryTable
                loading={query.isInitialLoading}
                paystubs={historyRows}
                onRowOpen={paystubId => goToDetails({ year, paystubId })}
              />
            )}
          </WSList>
        </WSList>
      )}
    </WSPage>
  );
};
